/**
 * The structured formula builder — column, operator, column, no typing (#60).
 *
 * Requirements: REQ-FX-014, REQ-FX-015, REQ-FX-016.
 *
 * Owner, 2026-08-21: *"These are based on columns, so it's column + operator +
 * column. Not text input."*
 *
 * Every control here is a `<select>` or a `<button>`. There is no text field
 * anywhere in the flow — not for the operands, not for the constants, and not
 * for the name, which the chain proposes for itself. A keyboard at the last
 * step would fail REQ-FX-014's "end-to-end using only selection controls" as
 * surely as one at the first.
 *
 * What the picker deliberately does not offer — parentheses, `%`, `**`,
 * arbitrary constants — is spec 060 §3.1.1, and all of it stays reachable
 * through free text (#44).
 */
import { useMemo, useState } from "preact/hooks";

import type { Registry } from "./api";
import type { BuilderOperator, Draft, Operand } from "./formula";
import {
  BUILDER_NUMBERS,
  BUILDER_OPERATORS,
  EMPTY_DRAFT,
  OPERATOR_LABEL,
  humaniseComputed,
  isComplete,
  numericAttributes,
  presets,
  suggestName,
  toDisplay,
  toFormula,
  uniqueName,
} from "./formula";
import type { Computed, Query } from "./query";
import { MAX_COLUMNS, shortLabel, withoutComputed } from "./query";

/** Enough to build a volume; more than that is a free-text formula. */
const MAX_TERMS = 5;
/** The API's own ceiling (spec 060 §4.1), so the UI stops before a 422 does. */
const MAX_COMPUTED = 8;

const encode = (o: Operand): string =>
  o.kind === "column" ? `col:${o.key}` : `num:${o.value}`;

function decode(raw: string): Operand | null {
  const [kind, rest] = [raw.slice(0, 3), raw.slice(4)];
  if (kind === "col" && rest) return { kind: "column", key: rest };
  if (kind === "num" && rest) {
    const value = Number(rest);
    return Number.isFinite(value) ? { kind: "number", value } : null;
  }
  return null;
}

/**
 * One operand chooser: every numeric spec, then the constants.
 *
 * Grouped by the registry's own groups rather than a flat list — the picker is
 * the same shape as every other attribute list in the app, and the grouping is
 * data rather than something written here (REQ-DM-002).
 */
function OperandSelect(props: {
  registry: Registry | null;
  value: Operand | null;
  onChange: (o: Operand) => void;
  label: string;
}) {
  const attrs = useMemo(() => numericAttributes(props.registry), [props.registry]);
  const groups = useMemo(() => {
    const m = new Map<string, typeof attrs>();
    for (const a of attrs) {
      const list = m.get(a.group) ?? [];
      list.push(a);
      m.set(a.group, list);
    }
    for (const list of m.values()) {
      list.sort((x, y) => shortLabel(x, x.key).localeCompare(shortLabel(y, y.key)));
    }
    return [...m.entries()].sort((x, y) => x[0].localeCompare(y[0]));
  }, [attrs]);

  return (
    <select
      class="select builder-operand"
      aria-label={props.label}
      value={props.value ? encode(props.value) : ""}
      onChange={(e) => {
        const o = decode((e.currentTarget as HTMLSelectElement).value);
        if (o) props.onChange(o);
      }}
    >
      <option value="" disabled>
        Choose a spec…
      </option>
      {groups.map(([group, list]) => (
        <optgroup key={group} label={titleCase(group)}>
          {list.map((a) => (
            <option key={a.key} value={`col:${a.key}`}>
              {shortLabel(a, a.key)}
            </option>
          ))}
        </optgroup>
      ))}
      <optgroup label="Numbers">
        {BUILDER_NUMBERS.map((n) => (
          <option key={n} value={`num:${n}`}>
            {n}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The builder and the list of columns already built.
 *
 * Held as a draft rather than written straight into the query: a half-built
 * chain is not a formula, and sending one on every tap would put a 422 on
 * screen while someone is still choosing.
 */
export function ComputedPanel(props: {
  registry: Registry | null;
  query: Query;
  onChange: (q: Query) => void;
}) {
  const { registry, query } = props;
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const available = useMemo(() => presets(registry), [registry]);
  const attrs = useMemo(() => numericAttributes(registry), [registry]);

  const labelOf = useMemo(() => {
    const byKey = new Map(attrs.map((a) => [a.key, a]));
    return (o: Operand): string =>
      o.kind === "number" ? String(o.value) : shortLabel(byKey.get(o.key), o.key);
  }, [attrs]);

  const full = query.computed.length >= MAX_COMPUTED;
  // REQ-WEB-022 / REQ-FX-011: a calculated column is a column shown, so it
  // counts against the same MAX_COLUMNS as the picker, the "N · max 8" readout
  // and the link. Adding past it put more on screen than a shared link carries.
  const rowFull = query.columns.length >= MAX_COLUMNS;
  const complete = isComplete(draft);
  const preview = complete ? toDisplay(draft, labelOf) : "";

  const add = () => {
    if (!complete || full || rowFull) return;
    const taken = new Set([
      ...query.computed.map((c) => c.name),
      ...attrs.map((a) => a.key),
    ]);
    const name = uniqueName(suggestName(draft), taken);
    const next: Computed = { name, formula: toFormula(draft) };
    props.onChange({
      ...query,
      computed: [...query.computed, next],
      // Shown the moment it exists. A column you have to go and enable in a
      // second panel is a column most people never see.
      columns: query.columns.includes(name) ? query.columns : [...query.columns, name],
    });
    setDraft(EMPTY_DRAFT);
  };

  return (
    <div class="builder">
      {query.computed.length ? (
        <ul class="builder-list">
          {query.computed.map((c) => (
            <li key={c.name} class="builder-made">
              <div class="builder-made-text">
                {/* The name already reads as the sum ("Length × Diameter");
                    the raw formula under it was code (#213). */}
                <span class="builder-made-name">{humaniseComputed(c.name)}</span>
              </div>
              <div class="builder-made-actions">
                <button
                  type="button"
                  class="chip"
                  aria-pressed={query.sortKey === c.name}
                  onClick={() =>
                    props.onChange({
                      ...query,
                      sortKey: c.name,
                      sortDesc: query.sortKey === c.name ? !query.sortDesc : true,
                    })
                  }
                >
                  {query.sortKey === c.name
                    ? query.sortDesc
                      ? "Highest first"
                      : "Lowest first"
                    : "Sort"}
                </button>
                <button
                  type="button"
                  class="filter-remove"
                  aria-label={`Remove ${c.name}`}
                  onClick={() => props.onChange(withoutComputed(query, c.name))}
                >
                  ✕
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {full ? (
        <p class="panel-hint">
          {MAX_COMPUTED} columns is the limit. Remove one to build another.
        </p>
      ) : rowFull ? (
        <p class="panel-hint" data-row-full>
          {MAX_COLUMNS} specs are already shown under each lens, and a shared link carries
          no more than that. Remove one under “Specs shown”, or remove a calculated column
          here, to build another.
        </p>
      ) : (
        <>
          {available.length && !draft.first ? (
            <div class="builder-presets">
              <p class="panel-hint">
                Make a new column from the specs you already see, such as Length × Diameter.
                Start from one of these, or build your own.
              </p>
              <div class="chips builder-preset-list">
                {available.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    class="chip builder-preset"
                    onClick={() => setDraft(p.draft)}
                  >
                    <span class="builder-preset-name">{p.label}</span>
                    {/* Visible, with the specs' own names: it used to be hover
                        text, which a phone cannot show (#213). */}
                    <span class="builder-preset-hint">
                      {toDisplay(p.draft, labelOf)}
                      {p.note ? `, ${p.note}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div class="builder-chain">
            <OperandSelect
              registry={registry}
              value={draft.first}
              label="First spec"
              onChange={(o) => setDraft((d) => ({ ...d, first: o }))}
            />

            {draft.rest.map((term, i) => (
              // The index is the identity here: terms have no stable key of
              // their own, and reordering is not offered.
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} class="builder-term">
                <select
                  class="select builder-op"
                  aria-label={`Operator ${i + 1}`}
                  value={term.op}
                  onChange={(e) => {
                    const op = (e.currentTarget as HTMLSelectElement).value as BuilderOperator;
                    setDraft((d) => ({
                      ...d,
                      rest: d.rest.map((t, j) => (j === i ? { ...t, op } : t)),
                    }));
                  }}
                >
                  {BUILDER_OPERATORS.map((op) => (
                    <option key={op} value={op}>
                      {OPERATOR_LABEL[op]}
                    </option>
                  ))}
                </select>
                <OperandSelect
                  registry={registry}
                  value={term.operand}
                  label={`Spec ${i + 2}`}
                  onChange={(o) =>
                    setDraft((d) => ({
                      ...d,
                      rest: d.rest.map((t, j) => (j === i ? { ...t, operand: o } : t)),
                    }))
                  }
                />
                <button
                  type="button"
                  class="filter-remove"
                  aria-label={`Remove term ${i + 1}`}
                  onClick={() =>
                    setDraft((d) => ({ ...d, rest: d.rest.filter((_, j) => j !== i) }))
                  }
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div class="builder-actions">
            <button
              type="button"
              class="chip"
              disabled={!draft.first || draft.rest.length >= MAX_TERMS}
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  // Multiply by default: it is the commonest next step and it
                  // is never destructive of what came before, unlike ÷.
                  // No operand: the new row starts on "Choose a spec…" and the
                  // column cannot be added until the person picks one.
                  rest: [...d.rest, { op: "*", operand: null }],
                }))
              }
            >
              + Add a spec
            </button>
            {draft.first ? (
              <button type="button" class="linkish" onClick={() => setDraft(EMPTY_DRAFT)}>
                Start over
              </button>
            ) : null}
          </div>

          {complete ? (
            <div class="builder-preview">
              {/* REQ-FX-015: the brackets the server will apply, shown rather
                  than assumed. `a + b × c` is unambiguous to a parser and
                  ambiguous to a person reading it on a phone. */}
              <p class="builder-preview-text">{preview}</p>
              <button type="button" class="button button-small" onClick={add}>
                Add column
              </button>
            </div>
          ) : draft.first ? (
            <p class="panel-hint">
              {draft.rest.length
                ? "Choose a spec in every row to finish the formula."
                : "Add a second spec to finish the formula."}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
